import type { Point } from '@os/engine';
import type { NewObject } from './commands';
import { newId } from './ids';
import { TABLE_MAX_COLS, TABLE_MAX_ROWS, TABLE_ROW_H } from './schema';
import { TABLE_COL_W } from './table';

/** Space between the tables of a split CSV (plan P11b Ruling 3). */
export const CSV_GAP = 80;
export const CSV_STICKY = 200;
const STICKY_GAP = 24;
const SECTION_PAD = 40;

export type CsvImportReport = { rows: number; tables: number; stickies: number; skippedEmpty: number };
type Item = { ref: string; data: NewObject };

const base = { rotation: 0, locked: false, style: {} };
const isEmpty = (r: readonly string[]) => r.every((c) => c.trim() === '');

/**
 * A parsed CSV as objects to insert in one step (backlog §17.4, plan P11b Ruling 3): `table` splits it into tables of
 * at most TABLE_MAX_ROWS × TABLE_MAX_COLS laid out in a grid (header repeated in each), nothing lost; `sticky` makes
 * one sticky per non-empty row, on a square grid inside a section named after the file. Centred on `at`.
 */
export function csvToObjects(
  rows: readonly (readonly string[])[],
  o: { destination: 'table' | 'sticky'; header: boolean; name: string; stickyColor: string; sectionFill: string; at: Point },
): { items: Item[]; report: CsvImportReport } {
  const head = o.header && rows.length > 0 ? rows[0]! : null;
  const body = o.header ? rows.slice(1) : rows;
  const data = body.filter((r) => !isEmpty(r));
  const report: CsvImportReport = { rows: data.length, tables: 0, stickies: 0, skippedEmpty: body.length - data.length };
  if (data.length === 0 && (o.destination === 'sticky' || !head)) return { items: [], report };
  const items = o.destination === 'table' ? tablesOf(head, data, o.at) : stickiesOf(head, data, o);
  report.tables = o.destination === 'table' ? items.length : 0;
  report.stickies = o.destination === 'sticky' ? items.length - 1 : 0;
  return { items, report };
}

function tablesOf(head: readonly string[] | null, data: readonly (readonly string[])[], at: Point): Item[] {
  const width = Math.max(head?.length ?? 0, ...data.map((r) => r.length), 1);
  const perTable = head ? TABLE_MAX_ROWS - 1 : TABLE_MAX_ROWS;
  const rowBlocks = Math.max(1, Math.ceil(data.length / perTable));
  const colBlocks = Math.ceil(width / TABLE_MAX_COLS);
  const colsIn = (cb: number) => Math.min(TABLE_MAX_COLS, width - cb * TABLE_MAX_COLS);
  const rowsIn = (rb: number) => (head ? 1 : 0) + Math.min(perTable, data.length - rb * perTable);
  const blockW = (cb: number) => colsIn(cb) * TABLE_COL_W;
  const blockH = (rb: number) => rowsIn(rb) * TABLE_ROW_H;
  let totalW = -CSV_GAP;
  for (let cb = 0; cb < colBlocks; cb++) totalW += blockW(cb) + CSV_GAP;
  let totalH = -CSV_GAP;
  for (let rb = 0; rb < rowBlocks; rb++) totalH += blockH(rb) + CSV_GAP;
  const items: Item[] = [];
  let y = at.y - totalH / 2;
  for (let rb = 0; rb < rowBlocks; rb++) {
    let x = at.x - totalW / 2;
    const slice = [...(head ? [head] : []), ...data.slice(rb * perTable, (rb + 1) * perTable)];
    for (let cb = 0; cb < colBlocks; cb++) {
      const cols = Array.from({ length: colsIn(cb) }, () => ({ id: newId(), w: TABLE_COL_W }));
      const tRows = slice.map((r) => ({
        id: newId(),
        cells: Object.fromEntries(cols.map((c, i) => [c.id, r[cb * TABLE_MAX_COLS + i] ?? ''])),
      }));
      items.push({ ref: newId(), data: { ...base, type: 'table', parentId: null, x, y, w: blockW(cb), h: blockH(rb), cols, rows: tRows } });
      x += blockW(cb) + CSV_GAP;
    }
    y += blockH(rb) + CSV_GAP;
  }
  return items;
}

/** A sticky's text: "Header: value" lines, the values alone without a header, or a FigJam sticky export's content. */
function stickyText(head: readonly string[] | null, r: readonly string[]): string {
  const creator = head ? head.findIndex((h) => h.trim().toLowerCase() === 'created by') : -1;
  if (head && creator !== -1) {
    const content = r.filter((c, i) => i !== creator && c.trim() !== '').join('\n');
    const by = r[creator]?.trim();
    return by ? `${content}\n— ${by}` : content;
  }
  return r
    .map((c, i) => [head?.[i]?.trim() ?? '', c] as const)
    .filter(([, c]) => c.trim() !== '')
    .map(([h, c]) => (h ? `${h}: ${c}` : c))
    .join('\n');
}

function stickiesOf(
  head: readonly string[] | null,
  data: readonly (readonly string[])[],
  o: { name: string; stickyColor: string; sectionFill: string; at: Point },
): Item[] {
  const cols = Math.ceil(Math.sqrt(data.length));
  const lines = Math.ceil(data.length / cols);
  const step = CSV_STICKY + STICKY_GAP;
  const w = 2 * SECTION_PAD + cols * step - STICKY_GAP;
  const h = 2 * SECTION_PAD + lines * step - STICKY_GAP;
  const section = newId();
  return [
    {
      ref: section,
      data: { ...base, type: 'section', parentId: null, x: o.at.x - w / 2, y: o.at.y - h / 2, w, h, name: o.name.slice(0, 200), fill: o.sectionFill, clip: false, collapsed: false },
    },
    // Children of a section are placed relative to it (graph.ts).
    ...data.map((r, i) => ({
      ref: newId(),
      data: {
        ...base, type: 'sticky' as const, parentId: section, x: SECTION_PAD + (i % cols) * step, y: SECTION_PAD + Math.floor(i / cols) * step,
        w: CSV_STICKY, h: CSV_STICKY, text: stickyText(head, r), color: o.stickyColor,
      },
    })),
  ];
}
